import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api, { getIdToken } from '../lib/api';
import useConfirmPurchase from '../lib/useConfirmPurchase';
import { PageHead, Section, Spinner, ErrorNote, Empty, useAsync } from '../components/ui';
import RichText from '../components/RichText';
import RichTextEditor from '../components/RichTextEditor';
import { cssBackground } from '../components/ColorField';
import ProgramHero from '../components/ProgramHero';
import RelatedStrips from '../components/RelatedStrips';
import { ReflectionResponse, AskInstructor } from './LessonPlayerPage';

// design/033 — answers typed before reflections were saved on the server
// lived only in this browser (localStorage key cp_pref_<blockId>). Upload
// each one once, then forget it locally.
const importedLocal = new Set();
async function importLocalReflections(practiceId, blocks) {
  let any = false;
  for (const b of blocks || []) {
    if (b.kind !== 'REFLECTION') continue;
    const key = `cp_pref_${b.id}`;
    if (importedLocal.has(key)) continue;
    importedLocal.add(key);
    let text = '';
    try { text = JSON.parse(localStorage.getItem(key) || 'null')?.text || ''; } catch { /* ignore */ }
    if (!text || !text.replace(/<[^>]*>/g, '').trim()) continue;
    try {
      await api.practiceSubmit(practiceId, { blockId: b.id, kind: 'REFLECTION', body: text });
      localStorage.removeItem(key);
      any = true;
    } catch { importedLocal.delete(key); /* try again next visit */ }
  }
  return any;
}
import '../styles/learn.css';

const htmlEmpty = (h) => !h || !h.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

/**
 * Practice player (design/019). Ongoing/repeatable — no progress tracking,
 * no "mark complete" button, no next-lesson link. If the caller doesn't have
 * access to a paid practice, we show the Buy panel instead of the blocks.
 */
export default function PracticePlayerPage({ preview = false } = {}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const fromHome = useLocation().state?.from === 'home';
  const [params, setParams] = useSearchParams();
  const { loading, error, data, reload } = useAsync(() => api.getLearnPractice(id), [id]);
  useConfirmPurchase(reload);  // grant now on return from Stripe; the poll below then sees it

  // Stripe redirect handling — same pattern as LearnCoursePage.
  const [buying, setBuying] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const checkoutStatus = params.get('checkout');
  const [confirming, setConfirming] = useState(checkoutStatus === 'success');
  const pollRef = useRef(null);

  // Server-backed per-learner notes (V38). A learner can create many notes
  // per Practice (journal style). The Notes panel shows saved notes at top
  // with edit/delete and timestamps; a composer at the bottom adds new ones.
  const [notes, setNotes] = useState([]);
  const reloadNotes = () => api.listPracticeNotes(id)
    .then((ns) => setNotes(Array.isArray(ns) ? ns : []))
    .catch(() => {});
  useEffect(() => { reloadNotes(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  // design/033 — the learner's reflections + questions (with replies).
  const [mySubs, setMySubs] = useState([]);
  const reloadSubs = () => (preview ? Promise.resolve() : api.practiceMySubmissions(id)
    .then((xs) => setMySubs(Array.isArray(xs) ? xs : []))
    .catch(() => {}));
  useEffect(() => { reloadSubs(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);
  // Opening the practice = the instructor's replies here are seen (2026-10-15).
  useEffect(() => {
    if (preview) return;
    api.practiceRepliesSeen(id).then(() => window.dispatchEvent(new Event('badge-refresh'))).catch(() => {});
  }, [id, preview]);
  useEffect(() => {
    if (preview || !data?.canAccess) return;
    importLocalReflections(id, data.blocks).then((any) => { if (any) reloadSubs(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, data?.canAccess]);

  useEffect(() => {
    if (checkoutStatus !== 'success') return;
    if (data?.canAccess) { setConfirming(false); return; }
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      try {
        const fresh = await api.getLearnPractice(id);
        if (fresh?.canAccess) { clearInterval(pollRef.current); setConfirming(false); reload(); }
      } catch { /* keep trying */ }
      if (tries >= 10) { clearInterval(pollRef.current); setConfirming(false); }
    }, 2000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutStatus, data?.canAccess]);

  if (loading) return <div className="page"><Spinner /></div>;
  if (error) return <div className="page"><ErrorNote error={error} onRetry={reload} /></div>;

  const priceLabel = data.paid
    ? (data.priceCents / 100).toLocaleString(undefined, { style: 'currency', currency: 'usd' })
    : 'Free';

  const buy = async () => {
    setBuying(true);
    try {
      const { url } = await api.learnPracticeCheckout(id);
      if (url) window.location.assign(url);
    } catch (e) { alert(e.message); setBuying(false); }
  };

  // Free practices are open to read; enrolling is optional and only adds it
  // to My Practices + the member list. Nothing enrolls automatically.
  const enroll = async () => {
    setEnrolling(true);
    try { await api.enrollLearnPractice(id); reload(); }
    catch (e) { alert(e.message); }
    finally { setEnrolling(false); }
  };

  const dismissBanner = () => {
    const p = new URLSearchParams(params); p.delete('checkout'); p.delete('session_id');
    setParams(p, { replace: true });
  };

  return (
    <div className="page">
      {!preview && (
        <PageHead
          breadcrumb={fromHome
            ? <a onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>My Practices</a>
            : <a onClick={() => navigate('/practices')} style={{ cursor: 'pointer' }}>Practices</a>}
          title=""
        />
      )}

      <ProgramHero kind="Practice" name={data.title}
        iconSrc={data.coverImageUrl ? api.iconUrl(data.coverImageUrl) : null}
        coverColor={data.headerColor}
        iconColor={data.iconColor}
        sub={(data.subtitle || data.instructorUsername) ? (
          <>
            {data.subtitle && <div>{data.subtitle}</div>}
            {data.instructorUsername && <div>Instructor: <b>@{data.instructorUsername}</b></div>}
          </>
        ) : null} />
      {/* 2026-10-16 — same "you're in" pill as groups / events. */}
      {!preview && data.enrolled && (
        <div style={{ margin: '0 0 12px' }}>
          <span className="pill" style={{ background: 'var(--green-bg, #dcfce7)', color: 'var(--green-text, #166534)', fontWeight: 700 }}>✓ You're enrolled</span>
        </div>
      )}

      {checkoutStatus === 'success' && data.canAccess && (
        <div className="section" style={{ background: 'var(--green-bg, #dcfce7)', border: '1px solid #86efac', borderRadius: 10, padding: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ flex: 1, color: '#166534' }}><strong>You have access.</strong> Payment received — open this practice as often as you like.</div>
          <button className="btn" onClick={dismissBanner}>Dismiss</button>
        </div>
      )}
      {checkoutStatus === 'success' && confirming && !data.canAccess && (
        <div className="section" style={{ background: 'var(--card-soft, #f9fafb)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, color: 'var(--muted)' }}>
          Confirming your payment… this can take a few seconds.
        </div>
      )}
      {checkoutStatus === 'cancel' && (
        <div className="section" style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ flex: 1, color: '#991b1b' }}>Checkout canceled — you haven't been charged.</div>
          <button className="btn" onClick={dismissBanner}>Dismiss</button>
        </div>
      )}

      {!data.paid && !data.enrolled && (
        <Section>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Free practice</div>
              <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                Enroll to keep it in My Practices and get updates.
              </div>
            </div>
            <button className="btn btn-primary" disabled={enrolling} onClick={enroll}>
              {enrolling ? 'Enrolling…' : 'Enroll'}
            </button>
          </div>
        </Section>
      )}

      {!data.canAccess && (
        <Section>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>{priceLabel} — one-time</div>
              <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
                Purchase to open this practice as many times as you want.
              </div>
            </div>
            <button className="btn btn-primary" disabled={buying} onClick={buy}>
              {buying ? 'Redirecting…' : `Buy — ${priceLabel}`}
            </button>
          </div>
        </Section>
      )}

      {data.canAccess && (
        <div className="section">
          {data.blocks && data.blocks.length > 0 ? (
            data.blocks.map((b) => (
              <div key={b.id} style={{ marginBottom: 18 }}>
                <PracticeBlockView block={b} practiceId={id} preview={preview}
                  mySubs={mySubs} onSubmitted={reloadSubs} />
              </div>
            ))
          ) : (
            <Empty>This practice has no content yet.</Empty>
          )}

        </div>
      )}

      {data.canAccess && !preview && (
        <AskInstructor mine={mySubs.filter((s) => s.kind === 'QUESTION')} onSubmitted={reloadSubs}
          send={(payload) => api.practiceSubmit(id, payload)} />
      )}

      {data.canAccess && !preview && (
        <Section title="📝 Your notes">
          <NotesList notes={notes} onChanged={reloadNotes} />
          <NoteComposer practiceId={id} onCreated={reloadNotes} />
        </Section>
      )}

      <RelatedStrips sourceKind="practice" sourceId={id} />
    </div>
  );
}

// Render one block. Text blocks use RichText; media blocks pull bytes from
// /learn/practice-blocks/{id}/media via an authenticated fetch and swap in
// an object URL so <audio>/<video>/<img> can play them without exposing the
// token in the URL bar.
function PracticeBlockView({ block, practiceId, preview = false, mySubs = [], onSubmitted }) {
  switch (block.kind) {
    case 'RICH_TEXT':
      return <RichText html={block.text} />;
    case 'REFLECTION':
      return preview
        ? <ReflectionPrompt block={block} />
        : (
          <ReflectionResponse blockId={block.id} prompt={block.text}
            mine={mySubs.filter((s) => s.kind === 'REFLECTION' && s.blockId === block.id)}
            onSubmitted={onSubmitted}
            send={(payload) => api.practiceSubmit(practiceId, payload)} />
        );
    case 'AUDIO':
      return block.hasMedia
        ? <MediaBlock block={block} kind="AUDIO" />
        : <MissingMedia kind="audio" />;
    case 'VIDEO':
      return block.hasMedia
        ? <MediaBlock block={block} kind="VIDEO" />
        : <MissingMedia kind="video" />;
    case 'DOC':
      if (!block.hasMedia) return null;
      return isImage(block)
        ? <MediaBlock block={block} kind="IMAGE" />
        : <MediaBlock block={block} kind="DOC" />;
    case 'TYPEFORM':
      return <FormEmbed url={block.text} />;
    case 'YOUTUBE':
      return <YouTubeEmbed url={block.text} />;
    default:
      return null;
  }
}

/**
 * Renders the list of saved notes for this practice (newest first) with
 * inline edit + delete. Each note is one row from the server; the parent
 * refetches via {@link reloadNotes} on every mutation.
 */
function NotesList({ notes, onChanged }) {
  if (!notes || notes.length === 0) {
    return <div style={{ color: 'var(--muted)', fontSize: '0.875rem', marginBottom: 12 }}>No notes yet. Write your first below.</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
      {notes.map((n) => <NoteItem key={n.id} note={n} onChanged={onChanged} />)}
    </div>
  );
}

function NoteItem({ note, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(note.body || '');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.updatePracticeNote(note.id, body); setEditing(false); onChanged?.(); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this note?')) return;
    setBusy(true);
    try { await api.deletePracticeNote(note.id); onChanged?.(); }
    catch (e) { alert(e.message); setBusy(false); }
  };
  const cancel = () => { setBody(note.body || ''); setEditing(false); };
  const stamp = note.updatedAt || note.createdAt;
  const edited = note.updatedAt && note.createdAt && note.updatedAt !== note.createdAt;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 12 }}>
        <span style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
          {edited ? 'Edited' : 'Saved'} · {stamp ? new Date(stamp).toLocaleString() : '—'}
        </span>
        {!editing && (
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-sm" disabled={busy} onClick={() => setEditing(true)}>Edit</button>
            <button className="btn btn-sm btn-danger" disabled={busy} onClick={remove}>Delete</button>
          </div>
        )}
      </div>
      {editing ? (
        <>
          <RichTextEditor value={body} onChange={setBody} minHeight={120} placeholder="Edit your note…" />
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" disabled={busy || htmlEmpty(body)} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
            <button className="btn btn-sm" disabled={busy} onClick={cancel}>Cancel</button>
          </div>
        </>
      ) : (
        <RichText html={note.body} />
      )}
    </div>
  );
}

/**
 * Composer for a new note. Clears itself on success so the learner can
 * immediately start another entry.
 */
function NoteComposer({ practiceId, onCreated }) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  // Bump `resetKey` on save-success so the editor's inner contentEditable
  // remounts (its useEffect-based value sync intentionally doesn't clobber
  // an actively-focused element, so we force a fresh mount).
  const [resetKey, setResetKey] = useState(0);
  const submit = async () => {
    if (htmlEmpty(body)) return;
    setBusy(true);
    try { await api.createPracticeNote(practiceId, body); setBody(''); setResetKey((k) => k + 1); onCreated?.(); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      <RichTextEditor key={resetKey} value={body} onChange={setBody} minHeight={120} placeholder="Write a new note…" />
      <div style={{ marginTop: 10 }}>
        <button className="btn btn-primary" disabled={busy || htmlEmpty(body)} onClick={submit}>
          {busy ? 'Saving…' : 'Save note'}
        </button>
      </div>
    </div>
  );
}

/** Reflection prompt only — the publisher's preview (no answer box). */
function ReflectionPrompt({ block }) {
  return (
    <div className="block-reflection">
      <div className="rlabel">📝 Reflection</div>
      <div className="rtext"><RichText html={block.text} /></div>
    </div>
  );
}

/**
 * Resolve a user-entered value to an embeddable URL. Typeform bare IDs
 * (~6-10 alphanumeric) get normalized to the canonical Typeform embed URL;
 * anything else that looks like an https URL is passed through so Tally,
 * Google Forms, Jotform, etc. all just work.
 */
function formEmbedUrl(raw) {
  if (!raw) return null;
  const s = raw.trim();
  if (!s) return null;
  if (/^[A-Za-z0-9]{4,20}$/.test(s)) return `https://form.typeform.com/to/${s}`;
  const tf = s.match(/typeform\.com\/to\/([A-Za-z0-9]+)/i);
  if (tf) return `https://form.typeform.com/to/${tf[1]}`;
  if (/^https?:\/\//i.test(s)) return s;
  return null;
}
function FormEmbed({ url }) {
  const embed = formEmbedUrl(url);
  if (!embed) return <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>⚠ Form link not recognized: {url || '(empty)'}</div>;
  return (
    <div style={{ position: 'relative', width: '100%', paddingBottom: '75%', height: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
      <iframe src={embed} title="Embedded form"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
        allow="camera; microphone; autoplay; encrypted-media; fullscreen" />
    </div>
  );
}

function youtubeEmbedUrl(raw) {
  if (!raw) return null;
  const s = raw.trim();
  if (!s) return null;
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return `https://www.youtube.com/embed/${s}`;
  const m = s.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([A-Za-z0-9_-]{11})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  return null;
}
function YouTubeEmbed({ url }) {
  const embed = youtubeEmbedUrl(url);
  if (!embed) return <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>⚠ YouTube link not recognized: {url || '(empty)'}</div>;
  return (
    <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', height: 0, borderRadius: 8, overflow: 'hidden', background: '#000' }}>
      <iframe src={embed} title="YouTube video"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        allowFullScreen />
    </div>
  );
}

function isImage(block) {
  const ct = block.mediaContentType || '';
  const name = block.mediaFilename || '';
  return /^image\//i.test(ct) || /\.(png|jpe?g|webp|gif)$/i.test(name);
}

function MissingMedia({ kind }) {
  return <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>⚠ No {kind} attached to this block.</div>;
}

// Fetch bytes with the auth header, hold them as a blob URL for the lifetime
// of this component, and revoke on unmount. For DOC blocks that aren't images
// we render a download link instead of trying to embed them.
function MediaBlock({ block, kind }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    (async () => {
      try {
        const token = await getIdToken();
        const res = await fetch(`${api.baseUrl}/learn/practice-blocks/${block.id}/media`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setUrl(objectUrl);
      } catch (e) {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [block.id]);

  if (failed) return <div style={{ color: 'var(--muted)' }}>⚠ Could not load {kind.toLowerCase()}.</div>;
  if (!url) return <Spinner />;

  if (kind === 'AUDIO') return <audio controls src={url} style={{ width: '100%' }} />;
  if (kind === 'VIDEO') return <video controls src={url} style={{ width: '100%', maxHeight: 520, background: '#000', borderRadius: 8 }} />;
  if (kind === 'IMAGE') return <img src={url} alt={block.mediaFilename || ''} style={{ maxWidth: '100%', borderRadius: 8 }} />;
  // DOC (non-image): offer a download link.
  return (
    <a href={url} download={block.mediaFilename || 'attachment'} className="btn">
      📄 Download {block.mediaFilename || 'attachment'}
    </a>
  );
}
